import type { ApiClient } from '../api/client';
import { META, readMeta, writeMeta } from '../db/schema';
import type { AppDb, MirrorWindow, MirroredAppointment } from '../db/schema';
import {
  Appointment,
  AppointmentDay,
  BlackoutList,
  CertificateList,
  ClientCredit,
  ClientDetail,
  ClientList,
  CreditOutstanding,
  Hours,
  NewsletterSenderConfigured,
  ProductList,
  ReservationPresence,
  ResourceList,
  ServiceList,
  Session,
} from '../wire';
import type {
  Blackout,
  Certificate,
  ClientDetail as ClientDetailType,
  Hours as HoursType,
  Product,
  Resource,
  Service,
  Session as SessionType,
} from '../wire';
import type { MirroredCredit } from '../db/schema';
import { addDays, dayDistance, isoDayIn } from './time';
import { ApiFailure } from '../api/errors';

/**
 * The mirror — spec §8.1.
 *
 * > The PWA mirrors the tenant's working set — today plus a window of
 * > appointments, the client book, services, products, open certificates — into
 * > local storage, refreshed on connect and by SSE while online. Offline, every
 * > read is served from the mirror and the UI says plainly how stale it is.
 *
 * Two rules hold this together and both are enforced by the shape of the class
 * rather than by care:
 *
 * **Every read in the app goes through here, and none of them touch the
 * network.** `today()`, `clients()`, `services()` and the rest read IndexedDB and
 * only IndexedDB. There is no "fetch if online" fallback, because a fallback is
 * a code path that is only exercised when there *is* a network — which is the
 * condition under which offline bugs are invisible. The views cannot tell
 * whether the iPad has a connection, which is why they cannot break when it
 * does not.
 *
 * **Freshness has exactly two sources: `hydrate` and `apply`.** Hydrate is the
 * connect-time refill. Apply is one SSE event turning into one targeted refetch.
 * Both stamp `lastSyncAt`, which is the number the status strip renders, so the
 * staleness the owner reads is the real age of the newest fact on the device and
 * not a timer that ticks whether or not anything arrived.
 */

export interface MirrorOptions {
  db: AppDb;
  api: ApiClient;
  onChange: () => void;
  now?: () => number;
  pastDays?: number;
  futureDays?: number;
  /** Must stay under `core-api`'s `CALENDAR_MAX_WINDOW_DAYS` (default 31). */
  chunkDays?: number;
}

/**
 * **What the shell knows about this tenant beyond its modules** — the facts a
 * tab waits for before it is drawn (`App.tsx`'s `TabSpec.when`).
 *
 * A module says a tenant *may* use a screen; these say the screen has
 * something to be about. Both default to `false`, and `false` covers
 * "unknown": a fresh device, offline, has never asked, and a tab that appeared
 * and then vanished once the answer came back would be worse than one that
 * waits a sync to appear.
 */
export interface TenantFacts {
  /** The shop has had at least one reservation, in any status. Latched. */
  hadReservation: boolean;
  /** The last `GET /v1/newsletter/sender` said `configured: true`. Follows the latest answer. */
  newsletterConfigured: boolean;
}

interface NewsletterSenderFact {
  tenantId: string;
  configured: boolean;
}

export interface MirrorState {
  session: SessionType | null;
  lastSyncAt: string | null;
  cursor: string | null;
  window: MirrorWindow | null;
}

export class Mirror {
  private readonly db: AppDb;
  private readonly api: ApiClient;
  private readonly onChange: () => void;
  private readonly now: () => number;
  private readonly pastDays: number;
  private readonly futureDays: number;
  private readonly chunkDays: number;

  /**
   * The session, in memory as well as in IndexedDB.
   *
   * Not a second copy of the truth — IndexedDB is still the durable one, this is
   * written only where that one is and is never read back into it. What it buys
   * is a **synchronous** answer to the question a dozen components ask on their
   * first render: which modules is this tenant on, and what does their staff
   * theme say. Every one of those reads used to be an IndexedDB round trip that
   * resolved a paint later, so a screen briefly rendered as though the tenant had
   * bought nothing and had no branding, and copy that follows the branding
   * (`docs/lovespa-personality.md`) flickered from one sentence to another.
   *
   * `undefined` means "this process has not seen the session yet" and is told
   * apart from `null`, which means "there is nothing stored". A component seeds
   * its state from the snapshot and the effect corrects it either way, so a cold
   * open behaves exactly as it did.
   */
  private cachedSession: SessionType | null | undefined = undefined;

  constructor(options: MirrorOptions) {
    this.db = options.db;
    this.api = options.api;
    this.onChange = options.onChange;
    this.now = options.now ?? Date.now;
    this.pastDays = options.pastDays ?? 7;
    this.futureDays = options.futureDays ?? 60;
    this.chunkDays = options.chunkDays ?? 28;
  }

  /* ---------------------------------------------------------------- */
  /* Filling it                                                        */
  /* ---------------------------------------------------------------- */

  /**
   * Refill the whole working set. Called on sign-in and on every reconnect.
   *
   * The order is deliberate: the session first, because its `timezone` is what
   * every appointment's day index is computed in and a book indexed in the
   * wrong zone would put an 11 PM booking on the wrong day of the calendar.
   */
  async hydrate(): Promise<void> {
    const session = await this.api.request({
      method: 'GET',
      path: '/session',
      schema: Session,
    });
    await writeMeta(this.db, META.session, session);
    this.cachedSession = session;

    const timezone = session.tenant.timezone;
    const today = isoDayIn(timezone, this.now());
    const from = addDays(today, -this.pastDays);
    const to = addDays(today, this.futureDays);

    await Promise.all([
      this.refreshCatalog(),
      this.refreshClients(),
      this.refreshAppointments(from, to, timezone),
      this.refreshMoney(session.modules),
      this.refreshTenantFacts(session.tenant.tenant_id, session.modules),
    ]);

    await writeMeta(this.db, META.window, { from, to } satisfies MirrorWindow);
    await this.touch();
  }

  /**
   * **The session alone** (B10) — after the owner changes who may edit the
   * catalog, so every screen redraws its controls from the new answer without
   * a whole hydrate. The same write `hydrate` makes, and the same snapshot.
   */
  async refreshSession(): Promise<void> {
    const session = await this.api.request({
      method: 'GET',
      path: '/session',
      schema: Session,
    });
    await writeMeta(this.db, META.session, session);
    this.cachedSession = session;
    await this.touch();
  }

  async refreshCatalog(): Promise<void> {
    const [services, resources, hours, blackouts] = await Promise.all([
      this.api.request({ method: 'GET', path: '/services', schema: ServiceList }),
      this.api.request({ method: 'GET', path: '/resources', schema: ResourceList }),
      this.api.request({ method: 'GET', path: '/settings/hours', schema: Hours }),
      this.api.request({ method: 'GET', path: '/blackouts', schema: BlackoutList }),
    ]);

    const tx = this.db.transaction(['services', 'resources', 'blackouts'], 'readwrite');
    await tx.objectStore('services').clear();
    await tx.objectStore('resources').clear();
    await tx.objectStore('blackouts').clear();
    for (const service of services.services) await tx.objectStore('services').put(service);
    for (const resource of resources.resources) await tx.objectStore('resources').put(resource);
    for (const blackout of blackouts.blackouts) await tx.objectStore('blackouts').put(blackout);
    await tx.done;

    await writeMeta(this.db, META.hours, hours);
    await this.touch();
  }

  /**
   * The client book, **in detail** (`?detail=true`).
   *
   * It used to ask for summaries, and the consequence was quiet: a freshly
   * signed-in iPad had every guest's *name* and none of their birthdays, notes,
   * preferences or remembered lengths, because those arrived only through a
   * per-client refetch driven by an SSE event that had never fired. Three
   * screens read exactly those fields out of the mirror — the birthday line on
   * Today, the appointment tap's notes and allergies, the length the booking
   * sheet prefills — so all three were blank on the device most likely to be in
   * a tunnel. One wider read at hydrate fixes all three at once.
   *
   * The merge stays, and so does its rule: a row already in the mirror is never
   * *downgraded* by a later list. It now matters less (the list is the whole
   * row) and it still matters at all, because a server that has not shipped the
   * flag answers with summaries and this must not drop what the device knows.
   */
  async refreshClients(): Promise<void> {
    const list = await this.api.request({
      method: 'GET',
      path: '/clients',
      query: { limit: 500, include_archived: 'true', detail: 'true' },
      schema: ClientList,
    });

    const tx = this.db.transaction('clients', 'readwrite');
    for (const client of list.clients) {
      const existing = await tx.store.get(client.client_id);
      const merged: ClientDetailType = {
        ...(existing ?? emptyDetail(client.client_id)),
        ...client,
      };
      await tx.store.put(merged);
    }
    await tx.done;
    await this.touch();
  }

  /**
   * The book, paged.
   *
   * `core-api` **clamps** an over-wide window rather than rejecting it (it is
   * the same choice `provider-api` makes, and it is friendlier to a caller that
   * asks for a year). It also means a device that asked for 68 days and did not
   * notice would mirror the first 31 and believe it had the lot, showing an
   * empty calendar for October rather than an unknown one. So the window is cut
   * into chunks smaller than the clamp, here, where the arithmetic is visible.
   */
  async refreshAppointments(from: string, to: string, timezone: string): Promise<void> {
    const days: MirroredAppointment[] = [];
    let cursor = from;
    while (dayDistance(cursor, to) >= 0) {
      const chunkTo = minDay(addDays(cursor, this.chunkDays - 1), to);
      const page = await this.api.request({
        method: 'GET',
        path: '/appointments',
        query: { date_from: cursor, date_to: chunkTo },
        schema: AppointmentDay,
      });
      for (const appointment of page.appointments) {
        days.push({ ...appointment, day: isoDayIn(timezone, appointment.slot.start) });
      }
      cursor = addDays(chunkTo, 1);
    }

    const tx = this.db.transaction('appointments', 'readwrite');
    // Replace the window wholesale rather than upserting: a cancellation that
    // happened while the device was away is an *absence*, and an upsert cannot
    // represent an absence. The window is bounded, so this is cheap and it is
    // the only way the mirror converges on the server's book rather than on the
    // union of every book it has ever seen.
    for (const existing of await tx.store.getAll()) {
      if (dayDistance(from, existing.day) >= 0 && dayDistance(existing.day, to) >= 0) {
        await tx.store.delete(existing.appointment_id);
      }
    }
    for (const appointment of days) await tx.store.put(appointment);
    await tx.done;
    await this.touch();
  }

  /* ---------------------------------------------------------------- */
  /* The money working set (spec §8.1, MVP 4)                          */
  /* ---------------------------------------------------------------- */

  /**
   * Products, open certificates and every credit balance — the rest of what
   * §8.1 calls the working set.
   *
   * > The PWA mirrors the tenant's working set — today plus a window of
   * > appointments, the client book, services, **products, open certificates** —
   * > into local storage.
   *
   * Two things decide the shape of this method.
   *
   * **It is gated on the tenant's modules**, taken from the session that was
   * just fetched. A tenant without `certificates` gets a `501` from
   * `GET /v1/certificates` — that is the point of the gate — and asking anyway
   * would spend a request per hydrate to be told something the session already
   * said. Capabilities are a projection (spec §3.4); so is this.
   *
   * **It is best-effort.** Each read is caught individually and a failure leaves
   * that cache as it was. The book is the thing an owner cannot run the day
   * without; a certificate list that did not refresh must not be able to stop it
   * from landing, and the next cycle will try again. What it must never do is
   * *look* fresh — which it cannot, because every one of these caches is read
   * back through the same `lastSyncAt` the status strip renders.
   */
  async refreshMoney(modules: readonly string[]): Promise<void> {
    const jobs: Promise<void>[] = [];
    if (modules.includes('products')) jobs.push(this.refreshProducts());
    if (modules.includes('certificates')) jobs.push(this.refreshCertificates());
    if (modules.includes('credit')) jobs.push(this.refreshCreditBalances());
    await Promise.all(jobs.map((job) => job.catch(() => undefined)));
  }

  /* ---------------------------------------------------------------- */
  /* Tenant facts: what a tab waits for                                */
  /* ---------------------------------------------------------------- */

  /**
   * Ask the two questions the navigation waits on, and remember the answers.
   *
   * **Reservations** (owner's decision 4 of 2026-09-23): the tab is hidden
   * until the shop has had a reservation, because a solo studio with a shelf
   * of Eminence jars and no online shop yet was looking at an empty screen
   * every day. `GET /v1/reservations?limit=1` — the list route as it is, no
   * status filter, so a cancelled or expired one counts: a shop that has had
   * one has a reason to look. **Latched**: once true it is never asked again
   * and never unlearned, so the tab cannot flicker away in a tunnel or on the
   * day the last reservation is collected. A new reservation arriving over
   * the stream latches it too (`apply`).
   *
   * **Newsletter**: the tab is hidden until `GET /v1/newsletter/sender` says
   * `configured: true` — the platform's sender domain and link base are set —
   * because a composer that can never send is a form that wastes an evening.
   * **Not latched**: configuration can be taken away, and the latest answer is
   * the one that is true.
   *
   * Both are gated on the module that owns the route (`products`, `clients`)
   * for `refreshMoney`'s reason — asking a route the tenant has not bought is a
   * request spent to be told what the session already said — and both are
   * best-effort: a failed probe leaves the last answer where it was (unknown
   * stays hidden, a known answer stays known), and the next hydrate asks again.
   * Each answer is stored with the tenant it is about, so a device that signs
   * into a second tenant without a clear cannot inherit the first one's tabs.
   *
   * No server field for either, on purpose: both questions already have a
   * route whose answer is exactly the fact, and a `session.facts` block would
   * be a second place that had to agree with them.
   */
  async refreshTenantFacts(tenantId: string, modules: readonly string[]): Promise<void> {
    const jobs: Promise<void>[] = [];
    if (modules.includes('products')) jobs.push(this.probeReservations(tenantId));
    if (modules.includes('clients')) jobs.push(this.probeNewsletterSender(tenantId));
    await Promise.all(jobs.map((job) => job.catch(() => undefined)));
  }

  private async probeReservations(tenantId: string): Promise<void> {
    if ((await readMeta<string>(this.db, META.hadReservation)) === tenantId) return;
    const probe = await this.api.request({
      method: 'GET',
      path: '/reservations',
      query: { limit: 1 },
      schema: ReservationPresence,
    });
    if (probe.reservations.length > 0) await this.latchReservation(tenantId);
  }

  private async latchReservation(tenantId: string): Promise<void> {
    if ((await readMeta<string>(this.db, META.hadReservation)) === tenantId) return;
    await writeMeta(this.db, META.hadReservation, tenantId);
    this.onChange();
  }

  private async probeNewsletterSender(tenantId: string): Promise<void> {
    const sender = await this.api.request({
      method: 'GET',
      path: '/newsletter/sender',
      schema: NewsletterSenderConfigured,
    });
    const before = await readMeta<NewsletterSenderFact>(this.db, META.newsletterSender);
    if (before?.tenantId === tenantId && before.configured === sender.configured) return;
    await writeMeta(this.db, META.newsletterSender, {
      tenantId,
      configured: sender.configured,
    } satisfies NewsletterSenderFact);
    this.onChange();
  }

  /**
   * The facts, for the tenant the stored session is about. Local, like every
   * read here; a fact recorded against another tenant is not this one's.
   */
  async tenantFacts(): Promise<TenantFacts> {
    const session = await this.session();
    const tenantId = session?.tenant.tenant_id;
    if (tenantId === undefined) return { hadReservation: false, newsletterConfigured: false };
    const [reservation, sender] = await Promise.all([
      readMeta<string>(this.db, META.hadReservation),
      readMeta<NewsletterSenderFact>(this.db, META.newsletterSender),
    ]);
    return {
      hadReservation: reservation === tenantId,
      newsletterConfigured: sender?.tenantId === tenantId && sender.configured,
    };
  }

  async refreshProducts(): Promise<void> {
    const list = await this.api.request({
      method: 'GET',
      path: '/products',
      query: { include_inactive: 'true', limit: 500 },
      schema: ProductList,
    });
    const tx = this.db.transaction('products', 'readwrite');
    await tx.store.clear();
    for (const product of list.products) await tx.store.put(product);
    await tx.done;
    await this.touch();
  }

  /**
   * Every certificate, not just the open ones.
   *
   * §8.1 says "open certificates" and the difference matters at the counter: a
   * guest presenting a card that was spent last month should be told *"that has
   * nothing left on it"* rather than *"no certificate with that code"*, and a
   * mirror that only held open ones could only say the second. The set is
   * bounded by the same `limit` the server clamps to, and `state` is recomputed
   * locally on every render (`money/certificates.ts`) so a stale row cannot
   * claim to be live.
   */
  async refreshCertificates(): Promise<void> {
    const list = await this.api.request({
      method: 'GET',
      path: '/certificates',
      query: { state: 'all', limit: 500 },
      schema: CertificateList,
    });
    const tx = this.db.transaction('certificates', 'readwrite');
    await tx.store.clear();
    for (const certificate of list.certificates) await tx.store.put(certificate);
    await tx.done;
    await this.touch();
  }

  /**
   * Every balance the shop owes, in one read.
   *
   * `GET /v1/credit/outstanding` returns only clients who have a balance, so the
   * store is **replaced** rather than upserted: a balance that has been spent to
   * zero is an *absence* in the response, and an upsert cannot represent an
   * absence — it would leave the till offering credit that is gone. The history
   * a row may already hold is preserved across the replace, because it is still
   * true and it is expensive to fetch.
   */
  async refreshCreditBalances(): Promise<void> {
    const outstanding = await this.api.request({
      method: 'GET',
      path: '/credit/outstanding',
      query: { limit: 500 },
      schema: CreditOutstanding,
    });

    const tx = this.db.transaction('credit', 'readwrite');
    const histories = new Map<string, MirroredCredit['entries']>();
    for (const row of await tx.store.getAll()) {
      if (row.entries !== undefined) histories.set(row.client_id, row.entries);
    }
    await tx.store.clear();
    for (const row of outstanding.clients) {
      const entries = histories.get(row.client_id);
      await tx.store.put({
        client_id: row.client_id,
        cash_cents: row.cash_cents,
        gift_cents: row.gift_cents,
        ...(entries === undefined ? {} : { entries }),
      });
    }
    await tx.done;
    await this.touch();
  }

  /** One guest's ledger — balances and the history behind them. */
  async refetchCredit(clientId: string): Promise<void> {
    const credit = await this.api.request({
      method: 'GET',
      path: `/clients/${clientId}/credit`,
      query: { limit: 100 },
      schema: ClientCredit,
    });
    await this.db.put('credit', {
      client_id: clientId,
      cash_cents: credit.balances.cash_cents,
      gift_cents: credit.balances.gift_cents,
      entries: credit.entries,
    });
    this.onChange();
  }

  /* ---------------------------------------------------------------- */
  /* Keeping it fresh: one event, one targeted refetch                 */
  /* ---------------------------------------------------------------- */

  /**
   * Apply one event from the stream.
   *
   * Refetch rather than apply-the-payload, on purpose. The event payloads are
   * the *partner's* shapes (`serializeBooking` — the webhook body), not the
   * staff projection: no client name, no service name, no resource name, no
   * notes. Writing one into the mirror would put a row on the calendar that
   * renders as a blank line. The event is a **cue**, and one extra small GET on
   * a change somebody just made is a cost the plan's own acceptance —
   * *"two devices on the same tenant never see different books while online"* —
   * is happy to pay.
   */
  async apply(type: string, subjectId: string | null): Promise<void> {
    if (type.startsWith('reservation.')) {
      // Any event about a reservation means the shop has one: the tab's fact
      // is latched here as well as by the hydrate's probe, so the first
      // reservation a website takes draws the tab while the screen is open.
      const tenantId = (await this.session())?.tenant.tenant_id;
      if (tenantId !== undefined) await this.latchReservation(tenantId);
    }
    if (type.startsWith('booking.') && subjectId !== null) {
      await this.refetchAppointment(subjectId);
    } else if (type.startsWith('client.') && subjectId !== null) {
      await this.refetchClient(subjectId);
    } else if (type === 'catalog.updated') {
      // One version covers the whole `items` table (§7.3.1), so a retail price
      // change and a treatment price change arrive as the same event and both
      // caches are the projection that has to move.
      await this.refreshCatalog();
      await this.refreshProducts().catch(() => undefined);
    } else if (type.startsWith('certificate.')) {
      await this.refreshCertificates().catch(() => undefined);
    } else if (type === 'credit.added' && subjectId !== null) {
      // `subjectId` is the client — see `addCredit`'s `emitEvent`.
      await this.refetchCredit(subjectId).catch(() => undefined);
    } else if (type === 'settlement.recorded') {
      // A checkout can move a certificate's balance and spend a guest's credit
      // in one commit, so both caches are refreshed rather than guessed at. The
      // *appointment* it closed out is refetched by the settling intent on this
      // device (`runtime.ts`) and by the next hydrate on any other — the event
      // carries `booking_id` in its payload, which `apply` is not given.
      await this.refreshCertificates().catch(() => undefined);
      await this.refreshCreditBalances().catch(() => undefined);
    }
    await this.touch();
  }

  async refetchAppointment(appointmentId: string): Promise<void> {
    const session = await this.session();
    if (session === null) return;
    try {
      const appointment = await this.api.request({
        method: 'GET',
        path: `/appointments/${appointmentId}`,
        schema: Appointment,
      });
      await this.putAppointment(appointment, session.tenant.timezone);
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'not_found') {
        await this.db.delete('appointments', appointmentId);
        this.onChange();
        return;
      }
      throw e;
    }
  }

  async refetchClient(clientId: string): Promise<void> {
    const client = await this.api.request({
      method: 'GET',
      path: `/clients/${clientId}`,
      schema: ClientDetail,
    });
    await this.putClient(client);
  }

  /* ---------------------------------------------------------------- */
  /* Local writes — what a settled intent does to the mirror           */
  /* ---------------------------------------------------------------- */

  async putAppointment(appointment: Appointment, timezone: string): Promise<void> {
    await this.db.put('appointments', {
      ...appointment,
      day: isoDayIn(timezone, appointment.slot.start),
    });
    this.onChange();
  }

  async putClient(client: ClientDetailType): Promise<void> {
    await this.db.put('clients', client);
    this.onChange();
  }

  async putService(service: Service): Promise<void> {
    // `putProduct`'s rule: an echo without `images` keeps the stored list.
    const stored =
      service.images === undefined ? await this.db.get('services', service.service_id) : undefined;
    await this.db.put(
      'services',
      stored?.images === undefined ? service : { ...service, images: stored.images },
    );
    this.onChange();
  }

  /**
   * A person or a room, written back after a settled `resource.*` intent (S2).
   *
   * `putService`'s twin one table over. The Team screen reads `resources()`, so
   * an owner who renames somebody in a tunnel sees the old name until the intent
   * lands and this runs — which is the same honesty the rest of §8.2 has, rather
   * than an optimistic row that might never become true.
   */
  async putResource(resource: Resource): Promise<void> {
    await this.db.put('resources', resource);
    this.onChange();
  }

  async putBlackout(blackout: Blackout): Promise<void> {
    await this.db.put('blackouts', blackout);
    this.onChange();
  }

  async putHours(hours: HoursType): Promise<void> {
    await writeMeta(this.db, META.hours, hours);
    this.onChange();
  }

  /**
   * A write's echo does not carry `images` (B7, `ItemImages` in `wire.ts`), so
   * the stored list is kept when the row arrives without one: a retire or a
   * price edit must not take the photographs off the shelf screen.
   */
  async putProduct(product: Product): Promise<void> {
    const stored =
      product.images === undefined ? await this.db.get('products', product.product_id) : undefined;
    await this.db.put(
      'products',
      stored?.images === undefined ? product : { ...product, images: stored.images },
    );
    this.onChange();
  }

  async putCertificate(certificate: Certificate): Promise<void> {
    await this.db.put('certificates', certificate);
    this.onChange();
  }

  /**
   * Write balances back after a top-up, keeping the history that is already
   * there. The server returns the new balance with the entry it just wrote, so
   * the counter sees the right number without waiting for a round trip it has
   * already made.
   */
  async putCreditBalances(
    clientId: string,
    balances: { cash: number; gift: number },
  ): Promise<void> {
    const existing = await this.db.get('credit', clientId);
    await this.db.put('credit', {
      ...(existing ?? { client_id: clientId }),
      client_id: clientId,
      cash_cents: balances.cash,
      gift_cents: balances.gift,
    });
    this.onChange();
  }

  /* ---------------------------------------------------------------- */
  /* Reads. Local, always.                                             */
  /* ---------------------------------------------------------------- */

  async session(): Promise<SessionType | null> {
    const stored = (await readMeta<SessionType>(this.db, META.session)) ?? null;
    this.cachedSession = stored;
    return stored;
  }

  /**
   * The session as of the last read or write, with no `await`.
   *
   * `undefined` until this process has seen one — which is the honest answer on
   * a cold open, and the reason the hooks that use it still keep their effect.
   */
  sessionNow(): SessionType | null | undefined {
    return this.cachedSession;
  }

  async state(): Promise<MirrorState> {
    const [session, lastSyncAt, cursor, window] = await Promise.all([
      readMeta<SessionType>(this.db, META.session),
      readMeta<string>(this.db, META.lastSyncAt),
      readMeta<string>(this.db, META.cursor),
      readMeta<MirrorWindow>(this.db, META.window),
    ]);
    return {
      session: session ?? null,
      lastSyncAt: lastSyncAt ?? null,
      cursor: cursor ?? null,
      window: window ?? null,
    };
  }

  async day(isoDay: string): Promise<MirroredAppointment[]> {
    const rows = await this.db.getAllFromIndex('appointments', 'by-day', isoDay);
    return rows.sort((a, b) => a.slot.start.localeCompare(b.slot.start));
  }

  async appointment(id: string): Promise<MirroredAppointment | undefined> {
    return this.db.get('appointments', id);
  }

  /**
   * **One guest's visits, out of the window this device holds** — newest first.
   *
   * The appointment tap needs *"last visit: 1 August, signature facial"* and it
   * needs it with the door shut and no bars, which is the whole argument for
   * reading it here rather than calling `GET /v1/clients/{id}/visits`: a network
   * round trip per tap, on the screen most likely to be opened in a treatment
   * room.
   *
   * The honest limit is the mirror's own window — `pastDays` behind today, which
   * is a fortnight, not a lifetime. So an empty answer means *"nothing in the
   * days this device keeps"* and the sheet says exactly that rather than
   * "never". The full history is the client screen's purchases list (B3), which
   * is online and says so.
   *
   * A full scan of the `appointments` store rather than an index: the store
   * holds one bounded window of one tenant's book — a few hundred rows on the
   * spa this was written for — and a `by-client` index would be a second thing
   * to keep correct on every write for a read that happens on a tap.
   */
  async visits(clientId: string): Promise<MirroredAppointment[]> {
    const rows = await this.db.getAll('appointments');
    return rows
      .filter((row) => row.client.client_id === clientId)
      .sort((a, b) => b.slot.start.localeCompare(a.slot.start));
  }

  async clients(search?: string): Promise<ClientDetailType[]> {
    const rows = await this.db.getAll('clients');
    const needle = (search ?? '').trim().toLowerCase();
    const matched =
      needle === ''
        ? rows
        : rows.filter(
            (c) =>
              c.display_name.toLowerCase().includes(needle) ||
              c.identities.some((i) => i.identity.toLowerCase().includes(needle)),
          );
    return matched.sort((a, b) => a.display_name.localeCompare(b.display_name));
  }

  async client(id: string): Promise<ClientDetailType | undefined> {
    return this.db.get('clients', id);
  }

  async services(): Promise<Service[]> {
    const rows = await this.db.getAll('services');
    // The owner's order (her printed card's, for a seeded menu), then name, then
    // id — `listScheduledItems`' order. A row mirrored before `sort_order`
    // travelled reads 0, which falls through to the name order this used to be.
    return rows.sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        a.name.localeCompare(b.name) ||
        a.service_id.localeCompare(b.service_id),
    );
  }

  async resources(): Promise<Resource[]> {
    const rows = await this.db.getAll('resources');
    // The owner's own order first (S2), then name, then id — the same total
    // ordering `listResources` applies server-side, so the Team screen and the
    // list the server returns cannot disagree about who is first. A device
    // holding rows from an older build reads `sort_order: 0` everywhere, which
    // is exactly the name-then-id order this line used to produce.
    return rows.sort(
      (a, b) =>
        a.sort_order - b.sort_order ||
        a.name.localeCompare(b.name) ||
        a.resource_id.localeCompare(b.resource_id),
    );
  }

  async blackouts(): Promise<Blackout[]> {
    const rows = await this.db.getAll('blackouts');
    return rows.sort((a, b) => a.start.localeCompare(b.start));
  }

  async hours(): Promise<HoursType | null> {
    return (await readMeta<HoursType>(this.db, META.hours)) ?? null;
  }

  async products(): Promise<Product[]> {
    const rows = await this.db.getAll('products');
    return rows.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }

  /** Newest first: the card somebody is holding was probably sold recently. */
  async certificates(): Promise<Certificate[]> {
    const rows = await this.db.getAll('certificates');
    return rows.sort(
      (a, b) => b.issued_on.localeCompare(a.issued_on) || b.code.localeCompare(a.code),
    );
  }

  /**
   * What this guest has on account.
   *
   * Absent means **zero**, not unknown: `GET /v1/credit/outstanding` lists every
   * client with a balance, so a client who is not in it has none. Returning
   * zeroes rather than `null` is what lets the checkout offer credit without a
   * "we don't know yet" state that would be true for most of the book.
   */
  async credit(clientId: string): Promise<MirroredCredit> {
    return (
      (await this.db.get('credit', clientId)) ?? {
        client_id: clientId,
        cash_cents: 0,
        gift_cents: 0,
      }
    );
  }

  async cursor(): Promise<string | null> {
    return (await readMeta<string>(this.db, META.cursor)) ?? null;
  }

  async setCursor(cursor: string): Promise<void> {
    await writeMeta(this.db, META.cursor, cursor);
  }

  /** Stamp "the newest fact on this device arrived now". */
  async touch(): Promise<void> {
    await writeMeta(this.db, META.lastSyncAt, new Date(this.now()).toISOString());
    this.onChange();
  }
}

/** The earlier of two days. `dayDistance(a, b) >= 0` means `a` is not after `b`. */
function minDay(a: string, b: string): string {
  return dayDistance(a, b) >= 0 ? a : b;
}

function emptyDetail(clientId: string): ClientDetailType {
  return {
    client_id: clientId,
    display_name: '',
    archived: false,
    is_walk_in_account: false,
    tags: [],
    identities: [],
    visit_count: 0,
    last_visit_at: null,
    birthday: null,
    notes: null,
    sms_opt_in: false,
    // Off, with no provenance — which is the truth about a placeholder and
    // exactly what `SmsReminders` draws for it: no date invented, no source
    // implied.
    sms_opt_in_at: null,
    sms_opt_in_source: null,
    // Off, for the same reason and the safer one: a placeholder must never
    // cause somebody to be mailed.
    email_marketing_opt_in: false,
    email_marketing_opt_in_at: null,
    email_marketing_opt_in_source: null,
    // And never trusted by default, on a row nobody has read back yet: A5's
    // whole safety story is that trust is granted by an owner.
    may_book_online: false,
    preferences: [],
    durations: [],
    anonymized_at: null,
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
  };
}
